import React, { useState, useEffect } from 'react'
import { 
  Calendar, Plus, Edit2, Trash2, User, 
  Clock, AlertCircle, CheckCircle, XCircle,
  Briefcase, Home, Users as UsersIcon
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO, addDays } from 'date-fns'
import { KidExceptionCalendarView } from './KidExceptionCalendarView'
import { getCurrentTimeInTimezone } from '@/utils/timezoneUtils'

interface KidEvent {
  id: string
  user_id: string
  title: string
  type: 'work' | 'julies' | 'friends' | 'event' | 'sick' | 'vacation'
  start_date: string
  end_date: string
  all_day: boolean
  requires_coverage: boolean
  coverage_status?: 'pending' | 'covered' | 'bidding'
  covered_by?: string
  notice_days?: number
  bonus_points?: number
  notes?: string
  created_at: string
  approved: boolean
  approved_by?: string
}

interface Kid {
  id: string
  username: string
  display_name: string
  color_theme: string
}

const EVENT_TYPES = [
  { value: 'work', label: 'Work', icon: Briefcase, color: 'bg-blue-500' },
  { value: 'julies', label: "Other Parent's House", icon: Home, color: 'bg-purple-500' },
  { value: 'friends', label: 'Friends', icon: UsersIcon, color: 'bg-green-500' },
  { value: 'event', label: 'Event', icon: Calendar, color: 'bg-orange-500' },
  { value: 'sick', label: 'Sick', icon: AlertCircle, color: 'bg-red-500' },
  { value: 'vacation', label: 'Vacation', icon: Calendar, color: 'bg-yellow-500' }
]

const getOverrideTypeLabel = (type: string) => {
  const typeMap: any = {
    'at_toms': "At Home (Parent 1)",
    'at_julies': "At Home (Parent 2)",
    'friend': 'At Friend',
    'school': 'School Event',
    'work': 'Work',
    'event': 'Event/Activity',
    'other': 'Other'
  }
  return typeMap[type] || 'Unavailable'
}

const mapOverrideTypeToEventType = (overrideType: string) => {
  const typeMap: any = {
    'at_julies': 'julies',
    'friend': 'friends',
    'school': 'event',
    'work': 'work',
    'other': 'event'
  }
  return typeMap[overrideType] || 'event'
}

export const KidEventsManager: React.FC = () => {
  const [kids, setKids] = useState<Kid[]>([])
  const [selectedKid, setSelectedKid] = useState<string>('')
  const [events, setEvents] = useState<KidEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [editingEvent, setEditingEvent] = useState<KidEvent | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all')
  const [todayInTz, setTodayInTz] = useState<Date>(new Date())
  const [availableLocations, setAvailableLocations] = useState<Array<{value: string, label: string, type: string}>>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  
  const [newEvent, setNewEvent] = useState<Partial<KidEvent>>({
    title: '',
    type: 'event',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    all_day: true,
    requires_coverage: false,
    notes: '',
    approved: false
  })

  useEffect(() => {
    loadKids()
    initializeTimezone()
    loadAvailableLocations()
  }, [])

  const initializeTimezone = async () => {
    try {
      const tzDate = await getCurrentTimeInTimezone()
      setTodayInTz(tzDate)
      setNewEvent(prev => ({
        ...prev,
        start_date: format(tzDate, 'yyyy-MM-dd'),
        end_date: format(tzDate, 'yyyy-MM-dd')
      }))
    } catch (error) {
      console.error('Failed to get timezone:', error)
    }
  }

  useEffect(() => {
    if (selectedKid || (kids.length > 0 && !selectedKid)) {
      loadEvents()
    }
  }, [selectedKid, filter, kids])

  const loadKids = async () => {
    try {
      const users = await apiClient.get('/api/users')
      const usersArray = Array.isArray(users) ? users : (users?.users || [])
      setKids(usersArray.filter((u: any) => !u.is_parent && u.is_active))
    } catch (error) {
      console.error('Failed to load kids:', error)
      toast.error('Failed to load kids')
    }
  }

  const loadAvailableLocations = async () => {
    try {
      const locations = await apiClient.get('/api/availability/locations')
      setAvailableLocations(locations)
    } catch (error) {
      console.error('Failed to load locations:', error)
      // Fall back to some default locations if the endpoint fails
      setAvailableLocations([
        { value: 'home', label: 'Home', type: 'home' },
        { value: 'school', label: 'School', type: 'school' }
      ])
    }
  }

  const loadEvents = async () => {
    setLoading(true)
    try {
      // Get availability overrides for all kids
      const allEvents: any[] = []
      
      // If no kids loaded yet and no specific kid selected, wait
      if (!selectedKid && kids.length === 0) {
        setLoading(false)
        return
      }
      
      if (selectedKid) {
        // Get for specific kid
        const response = await apiClient.get(`/api/availability/overrides/${selectedKid}`)
        const mappedEvents = response.map((e: any) => ({
          id: e.id,
          user_id: e.user_id,
          title: e.reason || getOverrideTypeLabel(e.override_type),
          type: mapOverrideTypeToEventType(e.override_type),
          start_date: e.override_date,
          end_date: e.override_date,
          all_day: e.all_day,
          requires_coverage: e.coverage_status !== 'past',
          coverage_status: e.coverage_status,
          notes: e.location,
          created_at: e.created_at,
          approved: e.status === 'approved'
        }))
        setEvents(mappedEvents)
      } else {
        // Get for all kids
        for (const kid of kids) {
          const response = await apiClient.get(`/api/availability/overrides/${kid.username}`)
          const mappedEvents = response.map((e: any) => ({
            id: e.id,
            user_id: e.user_id,
            title: e.reason || getOverrideTypeLabel(e.override_type),
            type: mapOverrideTypeToEventType(e.override_type),
            start_date: e.override_date,
            end_date: e.override_date,
            all_day: e.all_day,
            requires_coverage: e.coverage_status !== 'past',
            coverage_status: e.coverage_status,
            notes: e.location,
            created_at: e.created_at,
            approved: e.status === 'approved'
          }))
          allEvents.push(...mappedEvents)
        }
        setEvents(allEvents)
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      toast.error('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const calculateBonusPoints = (startDate: string) => {
    const start = parseISO(startDate)
    const today = todayInTz
    const noticeDays = Math.floor((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    
    if (noticeDays >= 7) return { days: noticeDays, points: 15 }
    if (noticeDays >= 3) return { days: noticeDays, points: 10 }
    if (noticeDays >= 1) return { days: noticeDays, points: 5 }
    return { days: noticeDays, points: 0 }
  }

  const handleCreateEvent = async () => {
    if (!selectedKid) {
      toast.error('Please select a child')
      return
    }
    
    if (!newEvent.title) {
      toast.error('Please enter an event title')
      return
    }

    const { days, points } = calculateBonusPoints(newEvent.start_date!)
    
    try {
      // Map event type to override type
      const overrideTypeMap: any = {
        'julies': 'at_julies',
        'friends': 'friend',
        'work': 'work',
        'event': 'other',
        'sick': 'other',
        'vacation': 'other'
      }
      
      // Calculate date range - parse as local dates to avoid timezone issues
      const [startYear, startMonth, startDay] = newEvent.start_date.split('-').map(Number)
      const [endYear, endMonth, endDay] = (newEvent.end_date || newEvent.start_date).split('-').map(Number)
      
      const startDate = new Date(startYear, startMonth - 1, startDay)
      const endDate = new Date(endYear, endMonth - 1, endDay)
      const dates = []
      
      // Create dates in local timezone
      let currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        dates.push(format(currentDate, 'yyyy-MM-dd'))
        currentDate = new Date(currentDate)
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      // Create an override for each date
      let successCount = 0
      let errorCount = 0
      
      for (const date of dates) {
        try {
          await apiClient.post('/api/availability/overrides', {
            user_id: selectedKid,
            override_date: date,
            override_type: overrideTypeMap[newEvent.type] || 'other',
            reason: newEvent.title,
            location: selectedLocation || null,
            all_day: newEvent.all_day
          })
          successCount++
        } catch (error) {
          console.error(`Failed to create override for ${date}:`, error)
          errorCount++
        }
      }
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} override${successCount > 1 ? 's' : ''}`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to create ${errorCount} override${errorCount > 1 ? 's' : ''}`)
      }
      setShowAddEvent(false)
      setSelectedLocation('')
      setNewEvent({
        title: '',
        type: 'event',
        start_date: format(todayInTz, 'yyyy-MM-dd'),
        end_date: format(todayInTz, 'yyyy-MM-dd'),
        all_day: true,
        requires_coverage: false,
        notes: '',
        approved: false
      })
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create event')
    }
  }

  const handleUpdateEvent = async (eventId: string, updates: Partial<KidEvent>) => {
    try {
      // Convert kid event updates to availability override format
      const overrideUpdates: any = {}
      if (updates.title) overrideUpdates.reason = updates.title
      if (updates.notes) overrideUpdates.location = updates.notes
      if (updates.all_day !== undefined) overrideUpdates.all_day = updates.all_day

      await apiClient.patch(`/api/availability/overrides/${eventId}`, overrideUpdates)
      toast.success('Event updated successfully')
      setEditingEvent(null)
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to update event')
    }
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      await apiClient.delete(`/api/availability/overrides/${eventId}`)
      toast.success('Event deleted successfully')
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to delete event')
    }
  }

  const handleApproveEvent = async (eventId: string) => {
    try {
      await apiClient.patch(`/api/availability/overrides/${eventId}`, { status: 'approved' })
      toast.success('Event approved')
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve event')
    }
  }

  const filteredEvents = events.filter(event => {
    if (filter === 'pending') return !event.approved
    if (filter === 'approved') return event.approved
    return true
  })

  const getEventTypeInfo = (type: string) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0]
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Kid Events & Exceptions</h2>
        <p className="text-gray-600">Manage unavailability, events, and coverage</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <select
              value={selectedKid}
              onChange={(e) => setSelectedKid(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Kids</option>
              {kids.map(kid => (
                <option key={kid.id} value={kid.username}>{kid.display_name}</option>
              ))}
            </select>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Events</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
            </select>
            <div className="flex rounded-lg overflow-hidden border">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white'}`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1 ${viewMode === 'calendar' ? 'bg-blue-500 text-white' : 'bg-white'}`}
              >
                Calendar
              </button>
            </div>
          </div>
          <button
            onClick={() => setShowAddEvent(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </button>
        </div>
      </div>

      {/* Events List */}
      {viewMode === 'list' ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Child
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEvents.map(event => {
                const typeInfo = getEventTypeInfo(event.type)
                const kid = kids.find(k => k.id === event.user_id)
                return (
                  <tr key={event.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${typeInfo.color} bg-opacity-10 mr-3`}>
                          <typeInfo.icon className={`w-5 h-5 ${typeInfo.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {event.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {typeInfo.label}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {kid && (
                        <div className="flex items-center">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2"
                            style={{ backgroundColor: kid.color_theme }}
                          >
                            {kid.display_name[0]}
                          </div>
                          <span className="text-sm text-gray-900">{kid.display_name}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(event.start_date), 'MMM d')}
                      {event.end_date !== event.start_date && 
                        ` - ${format(parseISO(event.end_date), 'MMM d')}`
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {event.requires_coverage && (
                        <div className="text-sm">
                          {event.coverage_status === 'covered' ? (
                            <span className="flex items-center text-green-600">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Covered
                            </span>
                          ) : event.coverage_status === 'bidding' ? (
                            <span className="flex items-center text-yellow-600">
                              <Clock className="w-4 h-4 mr-1" />
                              Bidding
                            </span>
                          ) : (
                            <span className="flex items-center text-gray-500">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Pending
                            </span>
                          )}
                          {event.bonus_points && event.bonus_points > 0 && (
                            <span className="text-xs text-blue-600">
                              +{event.bonus_points} pts
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {event.approved ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Approved
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {!event.approved && (
                          <button
                            onClick={() => handleApproveEvent(event.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <KidExceptionCalendarView
          events={filteredEvents}
          kids={kids}
          selectedKid={selectedKid}
          onEventClick={(event) => setEditingEvent(event)}
        />
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Add Event/Exception
              {selectedKid && kids.find(k => k.id === selectedKid) && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  for {kids.find(k => k.id === selectedKid)?.display_name}
                </span>
              )}
            </h3>
            <div className="space-y-4">
              {!selectedKid && (
                <div>
                  <label className="block text-sm font-medium mb-1">Select Child *</label>
                  <select
                    value={selectedKid}
                    onChange={(e) => setSelectedKid(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Choose a child...</option>
                    {kids.map(kid => (
                      <option key={kid.id} value={kid.username}>{kid.display_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Work at Target"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date *</label>
                  <input
                    type="date"
                    value={newEvent.end_date}
                    onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newEvent.requires_coverage}
                  onChange={(e) => setNewEvent({ ...newEvent, requires_coverage: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm">Requires chore coverage</label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select location...</option>
                  {availableLocations.map(loc => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              {newEvent.start_date && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-sm">
                    <strong>Notice Period:</strong> {calculateBonusPoints(newEvent.start_date).days} days
                  </div>
                  <div className="text-sm">
                    <strong>Coverage Bonus:</strong> {calculateBonusPoints(newEvent.start_date).points} points
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {calculateBonusPoints(newEvent.start_date).days >= 3 
                      ? 'Auto-redistribution enabled'
                      : 'Requires bidding for coverage'
                    }
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddEvent(false)
                  setSelectedLocation('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEvent}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}